import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, Wand2, Eye, Pencil, Trash2, Upload, Loader2, CheckCircle2, FileJson, Users } from "lucide-react";
import { lessonDocumentSchema } from "@shared/lessonBlocks";

interface AdminLesson {
  id: string;
  title: string;
  slug: string;
  grade: number;
  subject: string;
  topic: string;
  description: string | null;
  emoji: string | null;
  estimatedMinutes: number;
  xpReward: number;
  status: "draft" | "published" | "archived";
  generationMethod: string;
  blockCount: number;
  checkCount: number;
  createdAt: string;
  updatedAt: string;
  stats: { started: number; completed: number };
}

interface Grade { id: string; gradeNumber: number; displayName: string; isActive: boolean }
interface Subject { id: string; gradeId: string; name: string; displayName: string; isActive: boolean }
interface Topic { id: string; subjectId: string; name: string; displayName: string; isActive: boolean }

const STATUS_BADGE: Record<string, string> = {
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  draft: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  archived: "bg-muted text-muted-foreground",
};

/** Canonical subject name used in lesson rows ("Language Arts 2" -> "Language Arts"). */
function canonicalSubject(name: string): string {
  const lower = name.toLowerCase();
  if (lower.startsWith("language arts") || lower === "english") return "Language Arts";
  if (lower.startsWith("math")) return "Mathematics";
  if (lower.startsWith("science")) return "Science";
  if (lower.startsWith("social")) return "Social Studies";
  return name.replace(/\s*\((internal|g\d+)\)\s*$/i, "").trim();
}

export default function AdminLessons() {
  const { toast } = useToast();
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterSubject, setFilterSubject] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [genOpen, setGenOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<AdminLesson | null>(null);
  const [editJson, setEditJson] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [importJson, setImportJson] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [gen, setGen] = useState({ grade: "4", subjectId: "", topicId: "", focus: "", title: "", publish: false });

  const { data: lessons = [], isLoading } = useQuery<AdminLesson[]>({
    queryKey: ["/api/admin/lessons", filterGrade, filterSubject, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterGrade !== "all") params.set("grade", filterGrade);
      if (filterSubject !== "all") params.set("subject", filterSubject);
      if (filterStatus !== "all") params.set("status", filterStatus);
      const res = await fetch(`/api/admin/lessons?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load lessons");
      return res.json();
    },
  });
  const { data: grades = [] } = useQuery<Grade[]>({ queryKey: ["/api/admin/grades"] });
  const { data: subjects = [] } = useQuery<Subject[]>({ queryKey: ["/api/admin/subjects"] });
  const { data: topics = [] } = useQuery<Topic[]>({ queryKey: ["/api/admin/topics"] });

  const genGrade = grades.find(g => String(g.gradeNumber) === gen.grade);
  const genSubjects = subjects.filter(s => s.gradeId === genGrade?.id && s.isActive);
  const genSubject = subjects.find(s => s.id === gen.subjectId);
  const genTopics = topics.filter(t => t.subjectId === gen.subjectId && t.isActive);
  const genTopic = topics.find(t => t.id === gen.topicId);
  const subjectNames = useMemo(() => Array.from(new Set(lessons.map(l => l.subject))).sort(), [lessons]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/lessons"] });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/lessons/generate", {
        grade: parseInt(gen.grade),
        subject: canonicalSubject(genSubject?.name || ""),
        topic: genTopic?.name,
        focus: gen.focus || undefined,
        title: gen.title || undefined,
        publish: gen.publish,
      });
      return res.json();
    },
    onSuccess: (data) => {
      invalidate();
      setGenOpen(false);
      toast({ title: "Lesson generated", description: `"${data.lesson.title}" saved as ${data.lesson.status}. ${data.attempts > 1 ? `(${data.attempts} attempts)` : ""}` });
      window.open(`/lessons/${data.lesson.slug}`, "_blank");
    },
    onError: (e: any) => toast({ title: "Generation failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => (await apiRequest("PATCH", `/api/admin/lessons/${id}/status`, { status })).json(),
    onSuccess: () => { invalidate(); toast({ title: "Status updated" }); },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/admin/lessons/${id}`),
    onSuccess: () => { invalidate(); toast({ title: "Lesson deleted" }); },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, doc }: { id: string; doc: any }) => (await apiRequest("PUT", `/api/admin/lessons/${id}`, doc)).json(),
    onSuccess: () => { invalidate(); setEditing(null); toast({ title: "Lesson saved" }); },
    onError: (e: any) => setEditError(e?.response?.issues?.join("\n") || e?.message || "Save failed"),
  });

  const importMutation = useMutation({
    mutationFn: async (doc: any) => (await apiRequest("POST", "/api/admin/lessons", doc)).json(),
    onSuccess: (data) => { invalidate(); setImportOpen(false); setImportJson(""); toast({ title: "Lesson imported", description: data.title }); },
    onError: (e: any) => setImportError(e?.response?.issues?.join("\n") || e?.message || "Import failed"),
  });

  const openEditor = async (lesson: AdminLesson) => {
    const res = await fetch(`/api/admin/lessons/${lesson.id}`, { credentials: "include" });
    const full = await res.json();
    const doc = {
      title: full.title, slug: full.slug, grade: full.grade, subject: full.subject, topic: full.topic,
      description: full.description, emoji: full.emoji, estimatedMinutes: full.estimatedMinutes, xpReward: full.xpReward,
      sortOrder: full.sortOrder, tags: full.tags || [], status: full.status, blocks: full.blocks,
    };
    setEditJson(JSON.stringify(doc, null, 2));
    setEditError(null);
    setEditing(lesson);
  };

  const validateDoc = (json: string): { ok: true; doc: any } | { ok: false; error: string } => {
    let raw: any;
    try { raw = JSON.parse(json); } catch (e: any) { return { ok: false, error: `Invalid JSON: ${e.message}` }; }
    const { status, ...rest } = raw;
    const parsed = lessonDocumentSchema.safeParse(rest);
    if (!parsed.success) {
      return { ok: false, error: parsed.error.issues.slice(0, 20).map(i => `${i.path.join(".") || "(root)"}: ${i.message}`).join("\n") };
    }
    return { ok: true, doc: { ...parsed.data, status } };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="w-6 h-6 text-primary" /> Interactive Lessons</h1>
          <p className="text-muted-foreground text-sm">Block-based lessons with sorting, matching, cloze, tap-the-word, number line and fraction checks. Students earn XP on completion.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setImportError(null); setImportOpen(true); }} data-testid="button-import-lesson"><Upload className="w-4 h-4 mr-2" /> Import JSON</Button>
          <Button onClick={() => setGenOpen(true)} data-testid="button-generate-lesson"><Wand2 className="w-4 h-4 mr-2" /> Generate with AI</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-3">
            <Select value={filterGrade} onValueChange={setFilterGrade}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {grades.map(g => <SelectItem key={g.id} value={String(g.gradeNumber)}>{g.displayName}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterSubject} onValueChange={setFilterSubject}>
              <SelectTrigger className="w-[190px]"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {["Mathematics", "Science", "Social Studies", "Language Arts", ...subjectNames.filter(s => !["Mathematics", "Science", "Social Studies", "Language Arts"].includes(s))].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground self-center">{lessons.length} lesson{lessons.length === 1 ? "" : "s"}</div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lesson</TableHead>
                    <TableHead>Grade / Subject / Topic</TableHead>
                    <TableHead>Blocks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lessons.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No lessons yet. Generate one with AI or import a JSON file.</TableCell></TableRow>
                  )}
                  {lessons.map(l => (
                    <TableRow key={l.id} data-testid={`row-lesson-${l.slug}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{l.emoji || "📘"}</span>
                          <div>
                            <p className="font-semibold">{l.title}</p>
                            <p className="text-xs text-muted-foreground">{l.slug} · {l.estimatedMinutes} min · {l.xpReward} XP · {l.generationMethod}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">Grade {l.grade}<br /><span className="text-muted-foreground">{l.subject} · {l.topic}</span></TableCell>
                      <TableCell className="text-sm">{l.blockCount} blocks<br /><span className="text-muted-foreground">{l.checkCount} checks</span></TableCell>
                      <TableCell><Badge className={`border-0 ${STATUS_BADGE[l.status]}`}>{l.status}</Badge></TableCell>
                      <TableCell className="text-sm"><span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {l.stats.started} started</span><span className="flex items-center gap-1 text-muted-foreground"><CheckCircle2 className="w-3.5 h-3.5" /> {l.stats.completed} done</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" title="Preview" onClick={() => window.open(`/lessons/${l.slug}`, "_blank")} data-testid={`button-preview-${l.slug}`}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" title="Edit JSON" onClick={() => openEditor(l)} data-testid={`button-edit-${l.slug}`}><Pencil className="w-4 h-4" /></Button>
                          {l.status === "published" ? (
                            <Button size="sm" variant="outline" onClick={() => statusMutation.mutate({ id: l.id, status: "draft" })}>Unpublish</Button>
                          ) : (
                            <Button size="sm" onClick={() => statusMutation.mutate({ id: l.id, status: "published" })} data-testid={`button-publish-${l.slug}`}>Publish</Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-destructive" title="Delete" onClick={() => { if (window.confirm(`Delete "${l.title}"? Student progress on it will be removed too.`)) deleteMutation.mutate(l.id); }}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5" /> Generate a lesson with AI</DialogTitle>
            <DialogDescription>Claude writes a full interactive lesson (12 to 18 blocks, 6+ checks) for the grade and topic you choose. It lands as a draft unless you publish immediately. Takes about a minute.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Grade</Label>
                <Select value={gen.grade} onValueChange={v => setGen(g => ({ ...g, grade: v, subjectId: "", topicId: "" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{grades.filter(g => g.isActive).map(g => <SelectItem key={g.id} value={String(g.gradeNumber)}>{g.displayName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Select value={gen.subjectId} onValueChange={v => setGen(g => ({ ...g, subjectId: v, topicId: "" }))}>
                  <SelectTrigger><SelectValue placeholder="Pick a subject" /></SelectTrigger>
                  <SelectContent>{genSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.displayName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Topic</Label>
              <Select value={gen.topicId} onValueChange={v => setGen(g => ({ ...g, topicId: v }))} disabled={!gen.subjectId}>
                <SelectTrigger><SelectValue placeholder="Pick a topic" /></SelectTrigger>
                <SelectContent>{genTopics.map(t => <SelectItem key={t.id} value={t.id}>{t.displayName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Focus (optional)</Label>
              <Input placeholder="e.g. equivalent fractions, or the water cycle" value={gen.focus} onChange={e => setGen(g => ({ ...g, focus: e.target.value }))} />
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input placeholder="Leave blank to let the AI choose" value={gen.title} onChange={e => setGen(g => ({ ...g, title: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={gen.publish} onCheckedChange={v => setGen(g => ({ ...g, publish: v }))} id="gen-publish" />
              <Label htmlFor="gen-publish">Publish immediately (otherwise saved as draft for review)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={() => generateMutation.mutate()} disabled={!genTopic || generateMutation.isPending} data-testid="button-generate-submit">
              {generateMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing lesson...</> : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit JSON dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileJson className="w-5 h-5" /> Edit lesson JSON</DialogTitle>
            <DialogDescription>{editing?.title}. Block reference: scripts/lessons/AUTHORING.md. The document is validated before saving.</DialogDescription>
          </DialogHeader>
          <Textarea value={editJson} onChange={e => setEditJson(e.target.value)} className="font-mono text-xs h-[55vh]" spellCheck={false} />
          {editError && <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-40 overflow-auto">{editError}</pre>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => {
              const v = validateDoc(editJson);
              if (!v.ok) { setEditError(v.error); return; }
              setEditError(null);
              saveMutation.mutate({ id: editing!.id, doc: v.doc });
            }} disabled={saveMutation.isPending}>{saveMutation.isPending ? "Saving..." : "Validate and save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import a lesson JSON</DialogTitle>
            <DialogDescription>Paste a lesson document (same format as scripts/lessons/**/*.json). It is saved as a draft unless the document sets "status": "published".</DialogDescription>
          </DialogHeader>
          <Textarea value={importJson} onChange={e => setImportJson(e.target.value)} className="font-mono text-xs h-[50vh]" placeholder='{ "title": "...", "grade": 4, "subject": "Mathematics", "topic": "Number", "description": "...", "blocks": [ ... ] }' spellCheck={false} />
          {importError && <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-40 overflow-auto">{importError}</pre>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              const v = validateDoc(importJson);
              if (!v.ok) { setImportError(v.error); return; }
              setImportError(null);
              importMutation.mutate(v.doc);
            }} disabled={importMutation.isPending}>{importMutation.isPending ? "Importing..." : "Validate and import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
